import styled from "@emotion/styled";
import { forwardRef, useImperativeHandle, useEffect, useState, useMemo, useRef } from "react";
import { i18n, useForm } from "src/common";
import { CheckboxInput, InputBirthDate, InputCity, InputOIB, InputStreet, InputText, Validation, CardWithTitle, checkIfOlderThan18, checkIfNotOlderThan, validateOIB } from "src/components";
import { FieldConfig, Form, FormRef } from "./form";
import dayjs from "dayjs";

// Contractor form types
export type ContractorFormItemType = 'firstName' | 'lastName' | 'birthDate' | 'oib' | 'city' | 'street' | 'zipCode' | 'houseNumber' | 'email' | 'contractorIsPassenger';
export const allContractorFormItems: Array<ContractorFormItemType> = ['firstName', 'lastName', 'birthDate', 'oib', 'city', 'street', 'zipCode', 'houseNumber', 'email', 'contractorIsPassenger'];

export interface ContractorFormValues {
  firstName: string;
  lastName: string;
  birthDate: string;
  oib: string;
  city: string;
  cityKompareId: string;
  street: string;
  streetKompareId: string;
  zipCode: number;
  houseNumber: string;
  email: string;
  contractorIsPassenger: boolean;
}

// Insured persons form types
export type InsuredPersonFormItemType = 'firstName' | 'lastName' | 'birthDate' | 'oib';
export const allInsuredPersonDataFormItems: Array<InsuredPersonFormItemType> = ['firstName', 'lastName', 'birthDate'];

export interface InsuredPersonInterface {
  firstName: string;
  lastName: string;
  birthDate: string;
  child: boolean;
  oib?: string;
}

export interface InsuredPersonsFormValues {
  insuredPersons: InsuredPersonInterface[];
}

// Combined form values
export interface ContractorAndInsuredPersonsFormValues {
  contractor: ContractorFormValues;
  insuredPersons: InsuredPersonsFormValues;
}

interface ContractorAndInsuredPersonsFormProps {
  contractorData?: ContractorFormValues;
  contractorFormItems?: Array<ContractorFormItemType>;
  insuredPersonsData?: InsuredPersonsFormValues;
  insuredPersonsFormItems?: Array<InsuredPersonFormItemType>;
  numOfInsuredAdults: number;
  numOfInsuredChildren: number;
  maxAge?: number;
  showInsuredPersonOib?: boolean;
  onSubmit?: (values: ContractorAndInsuredPersonsFormValues) => void;
}

export const InsuredPersonsFormItem = styled.div({
  marginBottom: '24px'
});

const checkIfYoungerThan18 = (birthDate: string): boolean => {
  const today = dayjs();
  const birth = dayjs(birthDate);
  return today.diff(birth, 'year') < 18;
};

export const ContractorAndInsuredPersonsForm = forwardRef<FormRef, ContractorAndInsuredPersonsFormProps>(({
  contractorData,
  contractorFormItems = allContractorFormItems,
  insuredPersonsData,
  insuredPersonsFormItems = allInsuredPersonDataFormItems,
  numOfInsuredAdults,
  numOfInsuredChildren,
  onSubmit,
  maxAge,
  showInsuredPersonOib = false
}, ref) => {
  const { formRef, getValues: getContractorValues, validateForm: validateContractorForm } = useForm();

  const totalInsured = numOfInsuredAdults + numOfInsuredChildren;

  // Initialize insured persons state
  const [insuredPersonsValues, setInsuredPersonsValues] = useState<InsuredPersonsFormValues>(() => ({
    insuredPersons: Array.from({ length: totalInsured }, (_, index) => {
      const existingPerson = insuredPersonsData?.insuredPersons?.[index];
      return {
        firstName: existingPerson?.firstName || '',
        lastName: existingPerson?.lastName || '',
        birthDate: existingPerson?.birthDate || '',
        child: index >= numOfInsuredAdults,
        oib: existingPerson?.oib || '',
      };
    })
  }));

  const [insuredPersonsErrors, setInsuredPersonsErrors] = useState<{ [key: string]: string }>({});

  // Track contractorIsPassenger state for re-renders
  const [isContractorPassenger, setIsContractorPassenger] = useState<boolean>(
    () => contractorData?.contractorIsPassenger ?? false
  );

  // Use ref to track latest contractor values to avoid stale closures
  const contractorValuesRef = useRef<ContractorFormValues | null>(null);

  // Update isContractorPassenger when contractorData prop changes
  useEffect(() => {
    if (contractorData?.contractorIsPassenger !== undefined) {
      setIsContractorPassenger(contractorData.contractorIsPassenger === true);
    }
  }, [contractorData?.contractorIsPassenger]);

  // Clear first insured person when contractorIsPassenger changes to false
  useEffect(() => {
    if (!isContractorPassenger && insuredPersonsValues.insuredPersons.length > 0) {
      setInsuredPersonsValues(prev => {
        const firstPerson = prev.insuredPersons[0];
        // Only clear if first person has contractor data (not empty)
        if (firstPerson.firstName || firstPerson.lastName || firstPerson.birthDate || firstPerson.oib) {
          return {
            ...prev,
            insuredPersons: prev.insuredPersons.map((person, index) =>
              index === 0
                ? { ...person, firstName: '', lastName: '', birthDate: '', oib: '' }
                : person
            )
          };
        }
        return prev;
      });
    }
  }, [isContractorPassenger]);

  // Sync contractor data to first insured person when contractorIsPassenger is true
  useEffect(() => {
    // Use requestAnimationFrame + setTimeout to ensure form state is fully updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Re-read contractor values to ensure we have the latest
        const contractorValues = getContractorValues() as ContractorFormValues;
        contractorValuesRef.current = contractorValues;

        if (isContractorPassenger && insuredPersonsValues.insuredPersons.length > 0) {
          setInsuredPersonsValues(prev => {
            const firstPerson = prev.insuredPersons[0];
            const contractorFirstName = contractorValues.firstName || '';
            const contractorLastName = contractorValues.lastName || '';
            const contractorBirthDate = contractorValues.birthDate || '';
            const contractorOib = contractorValues.oib || '';

            // Only update if values changed
            if (firstPerson.firstName !== contractorFirstName ||
              firstPerson.lastName !== contractorLastName ||
              firstPerson.birthDate !== contractorBirthDate ||
              firstPerson.oib !== contractorOib) {
              return {
                ...prev,
                insuredPersons: prev.insuredPersons.map((person, index) =>
                  index === 0
                    ? { ...person, firstName: contractorFirstName, lastName: contractorLastName, birthDate: contractorBirthDate, oib: contractorOib }
                    : person
                )
              };
            }
            return prev;
          });
        }
      }, 0);
    });
  }, [isContractorPassenger, getContractorValues, insuredPersonsValues.insuredPersons.length]);

  const handleInsuredPersonFieldChange = (personIndex: number, fieldName: keyof InsuredPersonInterface, value: string) => {
    setInsuredPersonsValues(prev => ({
      ...prev,
      insuredPersons: prev.insuredPersons.map((person, index) =>
        index === personIndex ? { ...person, [fieldName]: value } : person
      )
    }));

    const errorKey = `insuredPersons.${personIndex}.${fieldName}`;

    // Validate OIB field on change (like contractor OIB)
    if (fieldName === 'oib' && showInsuredPersonOib) {
      if (!value?.trim()) {
        setInsuredPersonsErrors(prev => ({
          ...prev,
          [errorKey]: i18n.t('components.forms.insuredPersons.oibRequired')
        }));
      } else if (!validateOIB(value.trim())) {
        setInsuredPersonsErrors(prev => ({
          ...prev,
          [errorKey]: i18n.t('components.forms.insuredPersons.oibValid')
        }));
      } else {
        // Clear error if valid
        setInsuredPersonsErrors(prev => {
          if (prev[errorKey]) {
            const { [errorKey]: _, ...rest } = prev;
            return rest;
          }
          return prev;
        });
      }
    } else {
      // Clear error for other fields
      setInsuredPersonsErrors(prev => {
        if (prev[errorKey]) {
          const { [errorKey]: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    }
  };

  const validateInsuredPersonsForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    insuredPersonsValues.insuredPersons.forEach((person, personIndex) => {
      if (insuredPersonsFormItems.includes('firstName') && !person.firstName.trim()) {
        newErrors[`insuredPersons.${personIndex}.firstName`] = i18n.t('components.forms.insuredPersons.firstNameRequired');
      }

      if (insuredPersonsFormItems.includes('lastName') && !person.lastName.trim()) {
        newErrors[`insuredPersons.${personIndex}.lastName`] = i18n.t('components.forms.insuredPersons.lastNameRequired');
      }

      if (insuredPersonsFormItems.includes('birthDate') && !person.birthDate) {
        newErrors[`insuredPersons.${personIndex}.birthDate`] = i18n.t('components.forms.insuredPersons.birthDateRequired');
      }

      if (insuredPersonsFormItems.includes('birthDate') && person.birthDate) {
        if (person.child && !checkIfYoungerThan18(person.birthDate)) {
          newErrors[`insuredPersons.${personIndex}.birthDate`] = i18n.t('components.forms.insuredPersons.childMustBeYoungerThan18');
        }
        if (!person.child && !checkIfOlderThan18(person.birthDate)) {
          newErrors[`insuredPersons.${personIndex}.birthDate`] = i18n.t('components.forms.insuredPersons.adultMustBeOlderThan18');
        }
        if (maxAge && !checkIfNotOlderThan(person.birthDate, maxAge)) {
          newErrors[`insuredPersons.${personIndex}.birthDate`] = i18n.t('components.forms.insuredPersons.birthDateNotOlderThan', { maxAge });
        }
      }

      // OIB validation (only when showInsuredPersonOib is true)
      if (showInsuredPersonOib) {
        if (!person.oib?.trim()) {
          newErrors[`insuredPersons.${personIndex}.oib`] = i18n.t('components.forms.insuredPersons.oibRequired');
        } else if (!validateOIB(person.oib.trim())) {
          newErrors[`insuredPersons.${personIndex}.oib`] = i18n.t('components.forms.insuredPersons.oibValid');
        }
      }
    });

    // Check for duplicate OIBs (only when showInsuredPersonOib is true)
    if (showInsuredPersonOib) {
      const oibCounts: { [key: string]: number[] } = {};
      insuredPersonsValues.insuredPersons.forEach((person, index) => {
        const oib = person.oib?.trim();
        if (oib) {
          if (!oibCounts[oib]) {
            oibCounts[oib] = [];
          }
          oibCounts[oib].push(index);
        }
      });

      Object.values(oibCounts).forEach((indices) => {
        if (indices.length > 1) {
          indices.forEach((index) => {
            // Only set error if there isn't already an error for this field
            if (!newErrors[`insuredPersons.${index}.oib`]) {
              newErrors[`insuredPersons.${index}.oib`] = i18n.t('components.forms.insuredPersons.oibDuplicate');
            }
          });
        }
      });
    }

    setInsuredPersonsErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCombinedSubmit = () => {
    if (validateContractorForm() && validateInsuredPersonsForm() && onSubmit) {
      const contractorValues = getContractorValues() as ContractorFormValues;
      onSubmit({
        contractor: contractorValues,
        insuredPersons: insuredPersonsValues
      });
    }
  };

  const handleCombinedValidateForm = (): boolean => {
    // Run both so errors show on every invalid field, not only the first form
    const isContractorValid = validateContractorForm();
    const areInsuredPersonsValid = validateInsuredPersonsForm();
    return isContractorValid && areInsuredPersonsValid;
  };

  const handleCombinedGetValues = (): ContractorAndInsuredPersonsFormValues => {
    return {
      contractor: getContractorValues() as ContractorFormValues,
      insuredPersons: insuredPersonsValues
    };
  };

  const handleSetFieldValue = (fieldName: string, value: any) => {
    const parts = fieldName.split('.');
    if (parts.length === 3 && parts[0] === 'insuredPersons') {
      const personIndex = parseInt(parts[1]);
      const field = parts[2] as keyof InsuredPersonInterface;
      if (!isNaN(personIndex) && personIndex >= 0 && personIndex < totalInsured) {
        handleInsuredPersonFieldChange(personIndex, field, value);
      }
    } else {
      formRef.current?.setFieldValue?.(fieldName, value, true);
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: handleCombinedSubmit,
    getValues: handleCombinedGetValues,
    validateForm: handleCombinedValidateForm,
    setFieldValue: handleSetFieldValue,
    scrollIntoView: () => { }
  }));

  // Sync contractor fields to first insured person
  const syncContractorToFirstInsuredPerson = () => {
    // Use requestAnimationFrame + setTimeout to ensure form state is fully updated before reading values
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Re-read contractor values to ensure we have the latest (including any pending form updates)
        const contractorValues = getContractorValues() as ContractorFormValues;
        contractorValuesRef.current = contractorValues;

        // Update isContractorPassenger state
        if (contractorValues?.contractorIsPassenger !== undefined) {
          setIsContractorPassenger(contractorValues.contractorIsPassenger === true);
        }

        // Sync to insured person if contractor is passenger
        const isPassenger = contractorValues?.contractorIsPassenger === true;
        if (isPassenger) {
          setInsuredPersonsValues(prev => {
            // Ensure we have insured persons
            if (prev.insuredPersons.length === 0) return prev;

            const firstPerson = prev.insuredPersons[0];
            const contractorFirstName = contractorValues.firstName || '';
            const contractorLastName = contractorValues.lastName || '';
            const contractorBirthDate = contractorValues.birthDate || '';
            const contractorOib = contractorValues.oib || '';

            // Only update if values changed
            if (firstPerson.firstName !== contractorFirstName ||
              firstPerson.lastName !== contractorLastName ||
              firstPerson.birthDate !== contractorBirthDate ||
              firstPerson.oib !== contractorOib) {
              return {
                ...prev,
                insuredPersons: prev.insuredPersons.map((person, index) =>
                  index === 0
                    ? { ...person, firstName: contractorFirstName, lastName: contractorLastName, birthDate: contractorBirthDate, oib: contractorOib }
                    : person
                )
              };
            }
            return prev;
          });
        }
      }, 0);
    });
  };

  const contractorFields: FieldConfig<ContractorFormValues>[] = useMemo(() => [
    {
      name: 'firstName',
      label: i18n.t('components.forms.contractor.firstName'),
      component: InputText,
      validations: [{ type: 'required', message: i18n.t('components.forms.contractor.firstNameRequired') }],
      onChange: syncContractorToFirstInsuredPerson
    },
    {
      name: 'lastName',
      label: i18n.t('components.forms.contractor.lastName'),
      component: InputText,
      validations: [{ type: 'required', message: i18n.t('components.forms.contractor.lastNameRequired') }],
      onChange: syncContractorToFirstInsuredPerson
    },
    {
      name: "birthDate",
      label: i18n.t("components.forms.contractor.birthDate"),
      component: InputBirthDate,
      validations: isContractorPassenger ? [
        { type: "required", message: i18n.t("components.forms.contractor.birthDateRequired") },
        { type: "validOlderThan18", message: i18n.t("components.forms.contractor.birthDateOlderThan18") },
        { type: "validNotOlderThan", message: i18n.t("components.forms.contractor.birthDateNotOlderThan", { maxAge: maxAge }), params: { maxAge: maxAge } },
      ] : [{ type: "required", message: i18n.t("components.forms.contractor.birthDateRequired") },
      { type: "validOlderThan18", message: i18n.t("components.forms.contractor.birthDateOlderThan18") },],
      onChange: syncContractorToFirstInsuredPerson
    },
    {
      name: "oib",
      label: i18n.t("components.forms.contractor.oib"),
      component: InputOIB,
      validations: [
        { type: "required", message: i18n.t("components.forms.contractor.oibRequired"), params: { optional: true } },
        { type: "validOIB", message: i18n.t("components.forms.contractor.oibValid"), params: { optional: true } }
      ],
      onChange: syncContractorToFirstInsuredPerson
    },
    {
      name: "city",
      label: i18n.t("components.forms.contractor.city"),
      component: InputCity,
      validations: [{ type: "required", message: i18n.t("components.forms.contractor.cityRequired") }] as Validation[],
      formatValue: (values: any) => (values.city || values.zipCode) && `${values.city}, ${values.zipCode}`,
    },
    {
      name: "street",
      label: i18n.t("components.forms.contractor.street"),
      component: InputStreet,
      componentProps: { cityKompareId: 'cityKompareId' },
      validations: [{ type: "required", message: i18n.t("components.forms.contractor.streetRequired") }] as Validation[]
    },
    {
      name: "houseNumber",
      label: i18n.t("components.forms.contractor.houseNumber"),
      component: InputText,
      validations: [{ type: "required", message: i18n.t("components.forms.contractor.houseNumberRequired") }] as Validation[]
    },
    {
      name: 'email',
      label: i18n.t('components.forms.contractor.email'),
      component: InputText,
      componentProps: { type: 'email', maxLength: 50, disabled: true },
      validations: [
        { type: 'required', message: i18n.t("components.forms.contractor.emailRequired") },
        { type: 'validMail', message: i18n.t("components.forms.contractor.emailValid") },
      ] as Validation[],
    },
    {
      name: 'contractorIsPassenger',
      label: i18n.t('components.forms.contractor.contractorIsPassenger'),
      component: CheckboxInput,
      componentProps: { left: true },
      onChange: () => {
        // Use a longer timeout to ensure all form updates are processed
        setTimeout(syncContractorToFirstInsuredPerson, 50)
      }
    }
  ].filter((field) => contractorFormItems.includes(field.name as ContractorFormItemType)), [contractorFormItems, isContractorPassenger, maxAge]);

  // Re-validate birthDate field when isContractorPassenger changes
  useEffect(() => {
    const contractorValues = getContractorValues() as ContractorFormValues;
    if (contractorValues?.birthDate) {
      // Trigger re-validation by setting the field value with validation flag
      formRef.current?.setFieldValue?.('birthDate', contractorValues.birthDate, true);
    }
  }, [isContractorPassenger, maxAge, getContractorValues]);

  // Get contractor values for rendering (only for field values, not for isContractorPassenger check)
  const contractorValues = getContractorValues();

  return (
    <>
      <CardWithTitle title={i18n.t("components.forms.contractor.title")}>
        <Form
          ref={formRef}
          fields={contractorFields}
          onSubmit={() => { }}
          initialValues={contractorData}
        />
      </CardWithTitle>

      {insuredPersonsValues.insuredPersons.map((person, personIndex) => {
        const isFirstPerson = personIndex === 0;
        const isDisabled = isFirstPerson && isContractorPassenger;

        // Get value from contractor if first person and contractor is passenger
        const getFieldValue = (field: 'firstName' | 'lastName' | 'birthDate' | 'oib') => {
          if (isFirstPerson && isContractorPassenger) {
            return contractorValues?.[field] || person[field];
          }
          return person[field];
        };

        return (
          <CardWithTitle
            title={person.child
              ? `${i18n.t('components.forms.insuredPersons.child', { index: personIndex + 1 })}`
              : `${i18n.t('components.forms.insuredPersons.insuredPerson')} ${personIndex + 1}`}
            key={personIndex}
          >
            {insuredPersonsFormItems.includes('firstName') && (
              <InsuredPersonsFormItem>
                <InputText
                  id={`insuredPersons-${personIndex}-firstName`}
                  label={i18n.t('components.forms.insuredPersons.firstName')}
                  value={getFieldValue('firstName')}
                  onChange={(value) => handleInsuredPersonFieldChange(personIndex, 'firstName', value)}
                  error={insuredPersonsErrors[`insuredPersons.${personIndex}.firstName`]}
                  disabled={isDisabled}
                />
              </InsuredPersonsFormItem>
            )}

            {insuredPersonsFormItems.includes('lastName') && (
              <InsuredPersonsFormItem>
                <InputText
                  id={`insuredPersons-${personIndex}-lastName`}
                  label={i18n.t('components.forms.insuredPersons.lastName')}
                  value={getFieldValue('lastName')}
                  onChange={(value) => handleInsuredPersonFieldChange(personIndex, 'lastName', value)}
                  error={insuredPersonsErrors[`insuredPersons.${personIndex}.lastName`]}
                  disabled={isDisabled}
                />
              </InsuredPersonsFormItem>
            )}

            {insuredPersonsFormItems.includes('birthDate') && (
              <InsuredPersonsFormItem>
                <InputBirthDate
                  id={`insuredPersons-${personIndex}-birthDate`}
                  label={i18n.t('components.forms.insuredPersons.birthDate')}
                  value={getFieldValue('birthDate')}
                  onChange={(value) => handleInsuredPersonFieldChange(personIndex, 'birthDate', value)}
                  error={insuredPersonsErrors[`insuredPersons.${personIndex}.birthDate`]}
                  disabled={isDisabled}
                />
              </InsuredPersonsFormItem>
            )}

            {showInsuredPersonOib && (
              <InsuredPersonsFormItem>
                <InputOIB
                  id={`insuredPersons-${personIndex}-oib`}
                  label={i18n.t('components.forms.insuredPersons.oib')}
                  value={getFieldValue('oib') || ''}
                  onChange={(value) => handleInsuredPersonFieldChange(personIndex, 'oib', value)}
                  error={insuredPersonsErrors[`insuredPersons.${personIndex}.oib`]}
                  disabled={isDisabled}
                />
              </InsuredPersonsFormItem>
            )}
          </CardWithTitle>
        );
      })}
    </>
  );
});